import { getErrorMessage } from './error';
import { clearAuthData, isTokenExpired } from './auth';

/**
 * Serviço de integração com a API de Pagamentos PIX
 */

// URL base da API - pode ser configurada via variável de ambiente
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://162.240.235.102:7006';

/**
 * Redireciona para login quando a sessão expira
 */
const handleSessionExpired = () => {
  clearAuthData();
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// #region agent log helper
const debugLog = (location: string, message: string, data: Record<string, unknown>, hypothesisId: string) => {
  try {
    fetch('http://127.0.0.1:7242/ingest/63016e22-83f2-43b9-b461-c49fc1971804', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: 'debug-session',
        runId: 'run3', // adjusting base URL + Auth path
        hypothesisId,
        location,
        message,
        data,
        timestamp: Date.now(),
      }),
    }).catch(() => {});
  } catch (_) {
    /* ignore */
  }
};
// #endregion

export interface Filtros {
  conta_id: string;
  data_inicio: string;
  data_fim: string;
  dataProcessamento?: string;
}

export interface RequisicaoLancamentos {
  filtros: {
    conta_id: string;
    data_inicio: string;
    data_fim: string;
  };
  dataProcessamento?: string;
}

export interface LancamentoDetalhe {
  id: number;
  descricao: string;
  documento: string;
  dataVencimento: string;
  valor: number;
}

export interface PessoaSemPix {
  pessoaId: number;
  pessoa: string;
  valorTotal: number;
  quantidadeLancamentos: number;
  lancamentos: LancamentoDetalhe[];
}

export interface Lancamento {
  pessoaId: number;
  pessoa: string;
  chavePix?: string | null;
  valorTotal: number;
  quantidadeLancamentos: number;
  lancamentosIds?: number[];
  dataVencimentoMaisProxima: string;
  conta: string;
  formaPagamento: string;
  documentos?: string[];
}

export interface RespostaLancamentos {
  total: number;
  tagFiltrada?: number;
  totalPessoas: number;
  pessoasComPix: number;
  pessoasSemPix: number;
  percentualSemPix: number;
  lancamentos: Lancamento[];
  lancamentosSemPix: PessoaSemPix[];
}

const authHeaders = (): Record<string, string> => {
  if (typeof window === 'undefined') return {};
  
  // Verificar se o token está expirado antes de fazer a requisição
  if (isTokenExpired()) {
    handleSessionExpired();
    return {};
  }
  
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Busca os lançamentos de pagamento com base nos filtros
 * @param filtros - Filtros de busca (conta_id, data_inicio, data_fim)
 */
export const buscarLancamentos = async (filtros: Filtros): Promise<RespostaLancamentos> => {
  // Remove dataProcessamento dos filtros para a busca de lançamentos
  const { dataProcessamento, ...filtrosBusca } = filtros;
  
  debugLog('api.ts:buscarLancamentos', 'call', { url: `${API_BASE_URL}/api/pagamento/lancamentos-pagamento`, filtros: filtrosBusca }, 'H2');
  const response = await fetch(`${API_BASE_URL}/api/pagamento/lancamentos-pagamento`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
    } as HeadersInit,
    body: JSON.stringify(filtrosBusca),
  });

  if (!response.ok) {
    debugLog('api.ts:buscarLancamentos', 'error', { status: response.status, statusText: response.statusText }, 'H2');
    
    // Se receber 401, redirecionar para login
    if (response.status === 401) {
      handleSessionExpired();
      throw new Error('Sessão expirada');
    }
    
    const message = await getErrorMessage(response);
    throw new Error(message);
  }

  const json = await response.json().catch(() => ({}));
  debugLog('api.ts:buscarLancamentos', 'success', { total: json?.total, pessoas: json?.totalPessoas }, 'H2');
  return json;
};

/**
 * Gera e faz download do arquivo CNAB240
 * @param filtros - Filtros de busca incluindo dataProcessamento
 */
export const downloadArquivoCNAB240 = async (filtros: Filtros): Promise<void> => {
  // Monta o corpo da requisição no formato esperado pelo backend para gerar arquivo
  const requisicao: RequisicaoLancamentos = {
    filtros: {
      conta_id: filtros.conta_id,
      data_inicio: filtros.data_inicio,
      data_fim: filtros.data_fim,
    },
  };

  // Adiciona dataProcessamento apenas se foi informada
  if (filtros.dataProcessamento) {
    requisicao.dataProcessamento = filtros.dataProcessamento;
  }

  debugLog('api.ts:downloadArquivo', 'call', { url: `${API_BASE_URL}/api/pagamento/gerar-arquivo-pagamento`, requisicao }, 'H2');
  const response = await fetch(`${API_BASE_URL}/api/pagamento/gerar-arquivo-pagamento`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
    } as HeadersInit,
    body: JSON.stringify(requisicao),
  });

  if (!response.ok) {
    debugLog('api.ts:downloadArquivo', 'error', { status: response.status, statusText: response.statusText }, 'H2');
    
    // Se receber 401, redirecionar para login
    if (response.status === 401) {
      handleSessionExpired();
      throw new Error('Sessão expirada');
    }
    
    const message = await getErrorMessage(response);
    throw new Error(message);
  }

  const blob = await response.blob();
  debugLog('api.ts:downloadArquivo', 'blob', { size: blob.size }, 'H2');

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `CNAB240_PIX_${new Date().getTime()}.txt`;
  document.body.appendChild(a);
  a.click();

  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

/**
 * Verifica se a API está online
 */
export const verificarStatusAPI = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/health`, {
      method: 'GET',
    });
    return response.ok;
  } catch (error) {
    console.error('API offline:', error);
    return false;
  }
};


